import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Seo, breadcrumbLd, faqLd, ORIGIN } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { cityIntro } from "@/lib/copy";
import { ArrowUpRight, Loader2 } from "lucide-react";

export default function CityHub() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.geoCity(slug).then(({ data }) => setData(data)).catch(() => setNotFound(true));
    api.products({ sort: "trending" }).then(({ data }) => setProducts(data.filter((p) => p.featured).slice(0, 4)));
  }, [slug]);

  if (notFound) return <div className="h-[50vh] flex flex-col items-center justify-center gap-4"><p className="font-display text-3xl">City not found</p><Link to="/schools" className="bg-ink text-ivory px-6 py-3 font-label text-[10px] rounded-sm">All Schools</Link></div>;
  if (!data) return <div className="h-[50vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-rosemetal" /></div>;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Schools", path: "/schools" },
    { name: `${data.county} County`, path: `/schools/county/${data.schools[0]?.county_slug || ""}` },
    { name: data.name, path: `/prom-dresses/city/${slug}` },
  ];
  const faqs = [
    { q: `Where can I buy prom dresses in ${data.name}, WI?`, a: `Heather's Boutique serves ${data.name}, Wisconsin with luxury prom, homecoming and graduation dresses and tuxedos, delivered with school-exclusive dress locks and AI styling.` },
    { q: `Do you offer virtual try-on for ${data.name} students?`, a: `Yes — ${data.name} students can upload a selfie and see themselves in any look before locking or buying it.` },
  ];

  return (
    <div data-testid="city-hub-page" className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-12">
      <Seo
        title={`Prom Dresses in ${data.name}, WI`}
        description={`Prom, homecoming & graduation dresses in ${data.name}, WI (${data.county} County). Lock your look and try on virtually.`}
        path={`/prom-dresses/city/${slug}`}
        jsonLd={[
          breadcrumbLd(crumbs),
          faqLd(faqs),
          { "@context": "https://schema.org", "@type": "ClothingStore", name: `Heather's Boutique — ${data.name}, WI`, description: `Prom, homecoming and graduation formalwear serving ${data.name}, Wisconsin.`, areaServed: { "@type": "City", name: data.name }, priceRange: "$$-$$$", address: { "@type": "PostalAddress", addressLocality: data.name, addressRegion: "WI", addressCountry: "US" } },
        ]}
      />
      <Breadcrumbs items={crumbs} />
      <p className="font-label text-[10px] text-rosemetal mb-2">{data.name} · {data.county} County · WI</p>
      <h1 className="font-display text-5xl sm:text-6xl mb-4">Prom Dresses in {data.name}</h1>
      <p className="font-body text-base text-muted-foreground max-w-2xl mb-6 leading-relaxed">{cityIntro(data.name, data.county, data.school_count)}</p>
      <div className="flex gap-3 mb-14">
        <Link to="/prom-dresses" className="bg-ink text-ivory px-6 py-3 font-label text-[10px] rounded-sm">Shop Prom</Link>
        <Link to="/homecoming-dresses" className="border border-ink px-6 py-3 font-label text-[10px] rounded-sm">Homecoming</Link>
      </div>

      {products.length > 0 && (
        <section className="mb-16">
          <h2 className="font-display text-3xl mb-6">Editorial picks for {data.name}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      <h2 className="font-display text-3xl mb-6">{data.name} high schools</h2>
      <div className="grid md:grid-cols-2 gap-4">
        {data.schools.map((s) => (
          <Link key={s.id} to={`/schools/${s.slug || s.id}`} data-testid={`school-${s.id}`} className="group border border-border rounded-sm p-6 hover:border-rosemetal transition-colors">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-2xl group-hover:text-rosemetal transition-colors leading-tight">{s.name}</h3>
                <p className="font-body text-xs text-muted-foreground mt-1.5">{s.mascot || "—"}{s.students ? ` · ${s.students} students` : ""}</p>
              </div>
              <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-rosemetal shrink-0" />
            </div>
            <div className="mt-4 h-1 rounded-full" style={{ background: s.primary_color }} />
          </Link>
        ))}
      </div>

      <section className="max-w-[900px] mt-20" data-testid="city-faq">
        <h2 className="font-display text-4xl mb-8">Prom in {data.name} — FAQ</h2>
        <div className="divide-y divide-border border-y border-border">
          {faqs.map((f, i) => (
            <details key={i} className="group py-5">
              <summary className="font-display text-xl cursor-pointer list-none flex justify-between items-center">{f.q}<span className="text-rosemetal text-2xl group-open:rotate-45 transition-transform">+</span></summary>
              <p className="font-body text-sm text-muted-foreground mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
